import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@copro/api';
import { useSpace } from '../../../hooks/useSpace';
import { keys as calendarKeys } from "../../calendar/api/keys";
import { keys as documentKeys } from "../../documents/api/keys";
import { syncOwnedDocument } from "../../documents/api/syncOwnedDocument";
import { CouncilMeetingUpdate } from '../types';
import { meetingKeys } from './getMeetings';

type Variables = {
  id: string;
  meeting: CouncilMeetingUpdate;
  // Omit both to leave the report untouched (e.g. editing the description only).
  reportUrl?: string | null;
  reportDocumentId?: string | null;
};

export function useUpdateMeeting() {
  const queryClient = useQueryClient();
  const { currentSpace } = useSpace();

  return useMutation({
    mutationFn: async ({ id, meeting, reportUrl, reportDocumentId }: Variables) => {
      const { error: updateError } = await apiClient
        .from('council_meetings')
        .update(meeting)
        .eq('id', id);

      if (updateError) throw updateError;

      // Only manage the report document when the caller passed a report intent.
      if (reportUrl !== undefined) {
        await syncOwnedDocument({
          currentDocumentId: reportDocumentId ?? null,
          fileUrl: reportUrl,
          name: meeting.title ?? 'Réunion CS',
          type: 'Réunion CS',
          ownerColumn: 'meeting_id',
          ownerId: id,
          spaceId: currentSpace!.id,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: calendarKeys.list() });
      // The report also lives in documents (unified list).
      queryClient.invalidateQueries({ queryKey: documentKeys.documents });
    },
  });
}
